/**
 * Свой слой перевода вместо react-i18next.
 *
 * Почему свой: языков два, витрина одна, и в репозитории нет ни одной i18n-
 * зависимости. Сто строк здесь тестируются имеющимся vitest и не тянут чужой
 * рантайм. Если однажды понадобится ленивая подгрузка десятка локалей — слой
 * меняется, а вызовы `t()` остаются прежними.
 */
import type { Catalog, Lang, Vars } from './types'

/** Формы множественного числа: русскому нужны три, английскому две. */
export type PluralForm = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other'

const RULES = new Map<Lang, Intl.PluralRules>()

/** Категория множественного числа по правилам локали (Intl, не свои if'ы). */
export function pluralCategory(lang: Lang, count: number): PluralForm {
  let rules = RULES.get(lang)
  if (!rules) {
    rules = new Intl.PluralRules(lang)
    RULES.set(lang, rules)
  }
  return rules.select(count) as PluralForm
}

/** Подставить `{name}` из vars. Незаполненное оставляем как есть — дыру видно. */
export function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name]
    return value === undefined || value === null ? whole : String(value)
  })
}

/**
 * Текст по ключу. `vars.count` включает выбор формы множественного числа: тогда
 * ищутся ключи `<key>.one` / `.few` / `.many` / `.other`, и только если ни одной
 * нет — сам `<key>`.
 *
 * Пропавший ключ возвращается как есть: пустое место на экране не отличить от
 * задуманной пустоты, а ключ сразу показывает, чего не хватает в каталоге.
 */
export function translate(catalog: Catalog, lang: Lang, key: string, vars?: Vars): string {
  const raw = pickForm(catalog, lang, key, vars)
  if (raw === undefined) return key
  return interpolate(raw, vars)
}

function pickForm(catalog: Catalog, lang: Lang, key: string, vars?: Vars): string | undefined {
  const count = vars?.count
  if (typeof count === 'number') {
    const form = pluralCategory(lang, count)
    const exact = catalog[`${key}.${form}`]
    if (exact !== undefined) return exact
    const other = catalog[`${key}.other`]
    if (other !== undefined) return other
  }
  return catalog[key]
}

/** Готовая функция перевода для одной локали (то, что видит компонент как `t`). */
export type TFunction = (key: string, vars?: Vars) => string

export function makeT(catalog: Catalog, lang: Lang): TFunction {
  return (key, vars) => translate(catalog, lang, key, vars)
}
